import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActionSheetController, IonicModule, ModalController, ToastController } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { addOutline, arrowRedoOutline, callOutline, callSharp, closeCircleOutline, locationOutline, peopleCircleOutline, peopleOutline, personCircleOutline, phonePortraitOutline } from 'ionicons/icons';
import { CallingState, docStatus, DocTypeRelationName, GpAsteriskService, GpCallModel, GpCallMetaModel, TypeCall, MonitorMode, TransferTargetModel, CONSULT_CONFIRM_LABELS } from 'gp-asterisk';
import { DateTime } from 'luxon';
import { BehaviorSubject, firstValueFrom, interval, Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { ProfileState } from 'shared';
import { CallOrderModalComponent } from './call-order-modal.component';
import { SoftphoneService } from '../softphone/softphone.service';

addIcons({ addOutline, arrowRedoOutline, callOutline, callSharp, closeCircleOutline, locationOutline, peopleCircleOutline, peopleOutline, personCircleOutline, phonePortraitOutline });

@Component({
  selector: 'mobile-call-card',
  templateUrl: './call-card.component.html',
  styleUrls: ['./call-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, AsyncPipe, DatePipe, NgClass]
})
export class CallCardComponent implements OnInit, OnDestroy {
  @Input({ required: true }) call: GpCallModel | GpCallMetaModel;
  protected readonly CONSULT_CONFIRM_LABELS = CONSULT_CONFIRM_LABELS;

  readonly talkTime$ = new BehaviorSubject<string>('');
  private subscription = new Subscription();

  isShowDetail = false;

  protected readonly CallingState = CallingState;
  protected readonly TypeCall = TypeCall;
  protected readonly DocTypeRelationName = DocTypeRelationName;
  protected readonly docStatus = docStatus;

  constructor(
    private asteriskService: GpAsteriskService,
    private cdr: ChangeDetectorRef,
    private modalController: ModalController,
    private actionSheetController: ActionSheetController,
    private toastController: ToastController,
    private store: Store,
    private softphone: SoftphoneService
  ) {}

  ngOnInit() {
    this.subscription.add(
      interval(1000).subscribe(() => {
        const sec = DateTime.now()
          .diff(DateTime.fromISO(this.call.createdAt), 'seconds').seconds;
        this.talkTime$.next(
          DateTime.fromMillis(sec * 1000).toUTC().toFormat('mm:ss')
        );
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get callerName(): string {
    const meta = this.call as GpCallMetaModel;
    return meta?.client?.name || this.call.nameCaller || null;
  }

  answer() {
    // вбудований телефон увімкнений — розмова приходить на лінію, яку відкриває застосунок; так можна прийняти
    // і прямий дзвінок, на який уже дзвонить SIP-телефон (він замовкне)
    const viaApp = this.softphone.isActive;
    if (!this.canAnswer) return;
    const label = this.callerName || this.call.phone;
    const request = () => firstValueFrom(viaApp
      ? this.asteriskService.answerFromSoftphone(this.call.id)
      : this.asteriskService.answerOnCall(this.call));
    this.softphone.withLine(label, request, false,
      { direction: 'incoming', id: this.call.id, number: this.call.phone, name: this.callerName || undefined }).catch(async err => {
      const toast = await this.toastController.create({
        message: err?.error?.message || err?.message || 'Не вдалося прийняти дзвінок',
        color: 'danger', duration: 3000, position: 'top'
      });
      await toast.present();
    });
  }

  // Дзвінок у черзі (вихідна теж може повернутись у чергу, якщо після переадресації ніхто не відповів) або
  // дзвінок, що зараз дзвонить на мій SIP-телефон, — його можна перехопити вбудованим телефоном
  get canAnswer(): boolean {
    return this.call.status === CallingState.WaitingForAnAnswer
      || (this.call.status === CallingState.Ringing && this.softphone.isActive && this.isMyCall);
  }

  // Активна розмова (вхідна чи вихідна), клієнт уже з'єднаний з оператором
  get canTransfer(): boolean {
    return this.call.status === CallingState.Answered && !this.call.isDial;
  }

  // Переводити може той, хто веде розмову, і лише коли інше переведення не триває
  get canStartTransfer(): boolean {
    return this.canTransfer && !this.call.transfer && (!this.mySipNumber || this.isMyCall);
  }

  private get mySipNumber(): string | undefined {
    return this.store.selectSnapshot(ProfileState.currentUser)?.code || undefined;
  }

  private get isMyCall(): boolean {
    return !!this.mySipNumber && this.call.SIP_number === this.mySipNumber;
  }

  private get isConferenceMember(): boolean {
    return !!this.mySipNumber && !!this.call.conferenceMembers?.includes(this.mySipNumber);
  }

  get canJoinConference(): boolean {
    return this.canTransfer && !this.isMyCall && !this.isConferenceMember;
  }

  // Скасувати може будь-який учасник — він лишається з клієнтом сам
  get canEndConference(): boolean {
    return this.canTransfer && !!this.call.conferenceMembers?.length && (this.isMyCall || this.isConferenceMember);
  }

  joinConference() {
    if (!this.mySipNumber) {
      this.showToast('У вашому профілі не вказано внутрішній номер', 'danger');
      return;
    }
    this.asteriskService.joinConference(this.call).subscribe({
      next: () => this.showToast('Зараз задзвонить ваш телефон — підніміть трубку', 'success'),
      error: err => this.showToast(err?.error?.message || 'Не вдалося підключитися до розмови', 'danger')
    });
  }

  endConference() {
    this.asteriskService.endConference(this.call).subscribe({
      next: () => this.showToast('Конференцію скасовано — розмову ведете ви', 'success'),
      error: err => this.showToast(err?.error?.message || 'Не вдалося скасувати конференцію', 'danger')
    });
  }

  async openTransfer() {
    await this.pickColleague('Переадресувати на', target => this.transfer(target.username, target.name));
  }

  // Запросити колегу в розмову: спершу режим, потім колега
  async openInvite() {
    const modes: { mode: MonitorMode; text: string }[] = [
      { mode: 'conference', text: 'Конференція — усі чують одне одного' },
      { mode: 'whisper', text: 'Суфлер — його чуєте лише ви' },
      { mode: 'listen', text: 'Слухач — чує розмову, його не чує ніхто' }
    ];
    const sheet = await this.actionSheetController.create({
      header: 'Запросити колегу',
      buttons: [
        ...modes.map(item => ({
          text: item.text,
          handler: () => {
            this.pickColleague('Кого запросити', target => this.invite(target, item.mode));
          }
        })),
        { text: 'Скасувати', role: 'cancel' }
      ]
    });
    await sheet.present();
  }

  // колегу можна покликати, якщо в нього є SIP-телефон у мережі або застосунок (дзвінок дійде push-ом)
  private async pickColleague(header: string, pick: (target: TransferTargetModel) => void) {
    let targets: TransferTargetModel[];
    try {
      targets = await firstValueFrom(this.asteriskService.getTransferTargets());
    } catch {
      await this.showToast('Не вдалося завантажити внутрішні номери', 'danger');
      return;
    }

    const previous = this.call.transferredFrom;
    const own = this.mySipNumber;
    const reachable = (target: TransferTargetModel) => (target.isRegistered || !!target.hasApp) && !target.inCall;
    const note = (target: TransferTargetModel) => target.inCall ? ' (у розмові)' : reachable(target) ? '' : ' (не в мережі)';
    const buttons = targets
      .filter(target => target.username !== this.call.SIP_number && target.username !== own)
      .sort((a, b) => Number(b.username === previous) - Number(a.username === previous))
      .map(target => ({
        text: `${target.username === previous ? '↩ ' : ''}${target.name} · ${target.username}${note(target)}`,
        disabled: !reachable(target),
        handler: () => {
          pick(target);
        }
      }));

    const sheet = await this.actionSheetController.create({
      header,
      buttons: [...buttons, { text: 'Скасувати', role: 'cancel' }]
    });
    await sheet.present();
  }

  private invite(target: TransferTargetModel, mode: MonitorMode) {
    this.asteriskService.inviteToCall(this.call, target.username, mode, target.name).subscribe({
      next: () => this.showToast(`Клієнт на утриманні. Дзвонимо: ${target.name}`, 'success'),
      error: err => this.showToast(err?.error?.message || 'Не вдалося запросити колегу', 'danger')
    });
  }

  private transfer(username: string, name: string) {
    this.asteriskService.transferCall(this.call, username, name).subscribe({
      next: () => this.showToast(`Клієнт на утриманні. Дзвонимо: ${name}`, 'success'),
      error: err => this.showToast(err?.error?.message || 'Не вдалося почати переведення', 'danger')
    });
  }

  // Підтвердити/скасувати може той, хто почав переведення
  get isTransferOwner(): boolean {
    return !!this.call.transfer && (!this.mySipNumber || this.call.transfer.from === this.mySipNumber);
  }

  completeTransfer() {
    this.asteriskService.completeTransfer(this.call).subscribe({
      next: () => this.showToast('Розмову передано', 'success'),
      error: err => this.showToast(err?.error?.message || 'Не вдалося підтвердити переведення', 'danger')
    });
  }

  cancelTransfer() {
    this.asteriskService.cancelTransfer(this.call).subscribe({
      next: () => this.showToast('Переведення скасовано — ви знову з клієнтом', 'success'),
      error: err => this.showToast(err?.error?.message || 'Не вдалося скасувати переведення', 'danger')
    });
  }

  private async showToast(message: string, color: 'success' | 'danger') {
    const toast = await this.toastController.create({ message, color, duration: 3000, position: 'top' });
    await toast.present();
  }

  async openCreateOrder() {
    const modal = await this.modalController.create({
      component: CallOrderModalComponent,
      componentProps: { call: this.call },
      breakpoints: [0, 0.75, 1],
      initialBreakpoint: 0.75
    });
    await modal.present();
  }
}
